import { useCallback, useEffect, useRef, useState } from "react";
import {
  FULLSCREEN_GEOMETRY,
  PLAYOUT_SNAP_TARGETS,
  SNAP_TOLERANCE,
  barThickness,
  detectSnapTarget,
  interpolateGeometry,
  snapPosition,
  windowRect,
  type PlayoutGeometry,
  type PlayoutSnapTarget,
  type EasingName,
} from "@production-hub/shared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Play, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

interface PlayoutGeometryEditorProps {
  /** Artwork shown as the backdrop you position against */
  imageUrl?: string | null;
  /** "video" renders the backdrop as a looping <video> for cut-ins */
  mediaType?: "still" | "video";
  /** Natural pixel size of the media, used to place a cut-in strip correctly */
  mediaWidth?: number;
  mediaHeight?: number;
  geometry: PlayoutGeometry;
  onChange: (geometry: PlayoutGeometry) => void;
  /** Switcher resolution, so bar thickness can be quoted in real pixels */
  frameWidth?: number;
  frameHeight?: number;
  easing?: EasingName;
  inDurationFrames?: number;
}

const FPS = 25;

/**
 * Visual editor for an L-shape.
 *
 * The uploaded artwork is the backdrop; you drag the program window over it
 * until it fills the notch. The window is locked to 16:9 (a SuperSource box
 * scales uniformly, so one `size` value drives both axes) and magnets to the
 * four corners.
 *
 * Snapping writes the exact flush center, so a snapped window is pixel
 * accurate - but it can be nudged off, because real artwork is rarely
 * mathematically flush. An unsnapped window is warned about rather than
 * blocked: it just isn't an L.
 */
export function PlayoutGeometryEditor({
  imageUrl,
  mediaType = "still",
  mediaWidth,
  mediaHeight,
  geometry,
  onChange,
  frameWidth = 1920,
  frameHeight = 1080,
  easing = "ease-in-out",
  inDurationFrames = 15,
}: PlayoutGeometryEditorProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<PlayoutGeometry | null>(null);

  const snapTarget = detectSnapTarget(geometry);
  const bars = barThickness(geometry.size, frameWidth, frameHeight);
  const shown = preview ?? geometry;
  const rect = windowRect(shown);

  // Height the media occupies once scaled to the frame width, as a percentage
  // of frame height. A full-frame video yields 100; a 1920x250 strip yields ~23.
  const videoHeightPercent =
    mediaWidth && mediaHeight
      ? Math.min(
          100,
          (mediaHeight / mediaWidth) * (frameWidth / frameHeight) * 100,
        )
      : 100;

  const applySnapOrFree = useCallback((x: number, y: number, size: number) => {
    // Magnet: if the drag lands near a corner, write the exact flush centre
    // rather than whatever pixel the pointer happened to be on.
    for (const target of PLAYOUT_SNAP_TARGETS) {
      const snapped = snapPosition(target, size);
      if (
        Math.abs(snapped.x - x) <= SNAP_TOLERANCE * 3 &&
        Math.abs(snapped.y - y) <= SNAP_TOLERANCE * 3
      ) {
        return { ...snapped, size };
      }
    }
    return { x, y, size };
  }, []);

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const bounds = canvas.getBoundingClientRect();
      const x = (event.clientX - bounds.left) / bounds.width;
      const y = (event.clientY - bounds.top) / bounds.height;

      onChange(
        applySnapOrFree(
          Math.min(1, Math.max(0, x)),
          Math.min(1, Math.max(0, y)),
          geometry.size,
        ),
      );
    },
    [applySnapOrFree, geometry.size, onChange],
  );

  useEffect(() => {
    if (!dragging) return;
    const stop = () => setDragging(false);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stop);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stop);
    };
  }, [dragging, handlePointerMove]);

  /** Resizing keeps the snapped corner pinned, so the L stays valid. */
  const handleSizeChange = (size: number) => {
    if (snapTarget) {
      onChange({ ...snapPosition(snapTarget, size), size });
    } else {
      onChange({ ...geometry, size });
    }
  };

  const nudge = (dx: number, dy: number) => {
    onChange({
      ...geometry,
      x: Math.min(1, Math.max(0, geometry.x + dx)),
      y: Math.min(1, Math.max(0, geometry.y + dy)),
    });
  };

  /**
   * Scrub the squeeze locally, using the same lerp the bridge will use, so the
   * timing can be judged without touching the switcher.
   */
  const playPreview = () => {
    if (preview) return;
    const frames = Math.max(1, inDurationFrames);
    const frameMs = 1000 / FPS;
    let frame = 0;

    const step = () => {
      frame += 1;
      setPreview(
        interpolateGeometry(
          FULLSCREEN_GEOMETRY,
          geometry,
          frame / frames,
          easing,
        ),
      );
      if (frame < frames) {
        setTimeout(step, frameMs);
      } else {
        setTimeout(() => setPreview(null), 800);
      }
    };
    setPreview(FULLSCREEN_GEOMETRY);
    setTimeout(step, frameMs);
  };

  return (
    <div className="space-y-4">
      <div
        ref={canvasRef}
        className="relative w-full overflow-hidden rounded-md border bg-muted"
        style={{ aspectRatio: "16 / 9" }}
        data-testid="playout-canvas"
      >
        {imageUrl ? (
          mediaType === "video" ? (
            // The real playout comes from vMix; this local copy exists purely
            // so the window can be positioned against the actual footage.
            //
            // Cut-in spots are usually a strip (e.g. 1920x250), not a full
            // frame. Stretching one over the whole canvas would misrepresent
            // where it actually sits, so scale it to the frame width and pin it
            // to the bottom - which is where vMix composites it.
            <video
              src={imageUrl}
              className="absolute inset-x-0 bottom-0 w-full object-contain"
              style={{ height: `${videoHeightPercent}%` }}
              muted
              loop
              autoPlay
              playsInline
            />
          ) : (
            <img
              src={imageUrl}
              alt="L-shape artwork"
              className="absolute inset-0 h-full w-full object-cover"
              draggable={false}
            />
          )
        ) : (
          <div className="absolute inset-0 grid place-items-center text-sm text-muted-foreground">
            Upload the artwork to position the program window over it
          </div>
        )}

        {/* The program window. Dragging it sets the stored centre. */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Program window"
          onPointerDown={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onKeyDown={(event) => {
            const step = event.shiftKey ? 0.01 : 0.002;
            if (event.key === "ArrowLeft") nudge(-step, 0);
            else if (event.key === "ArrowRight") nudge(step, 0);
            else if (event.key === "ArrowUp") nudge(0, -step);
            else if (event.key === "ArrowDown") nudge(0, step);
            else return;
            event.preventDefault();
          }}
          className={cn(
            "absolute cursor-move border-2 bg-primary/10 backdrop-brightness-125",
            snapTarget ? "border-primary" : "border-destructive",
            dragging && "ring-2 ring-primary",
            preview && "pointer-events-none",
          )}
          style={{
            left: `${rect.left * 100}%`,
            top: `${rect.top * 100}%`,
            width: `${rect.width * 100}%`,
            height: `${rect.height * 100}%`,
          }}
          data-testid="playout-window"
        >
          <span className="absolute left-1 top-1 rounded bg-background/80 px-1 text-[10px] font-medium">
            Program
          </span>
        </div>
      </div>

      {!snapTarget && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            The window isn&apos;t flush against two frame edges, so this
            isn&apos;t an L-shape. Drag it into a corner, or keep it here if
            you&apos;re matching artwork deliberately.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Window size</Label>
          <span className="text-sm text-muted-foreground">
            {Math.round(geometry.size * 100)}%
          </span>
        </div>
        <Slider
          value={[geometry.size]}
          min={0.3}
          max={1}
          step={0.001}
          onValueChange={([value]) => handleSizeChange(value)}
          data-testid="playout-size"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {PLAYOUT_SNAP_TARGETS.map((target) => (
          <Button
            key={target}
            type="button"
            size="sm"
            variant={snapTarget === target ? "default" : "outline"}
            onClick={() =>
              onChange({
                ...snapPosition(target as PlayoutSnapTarget, geometry.size),
                size: geometry.size,
              })
            }
          >
            {target.replace("-", " ")}
          </Button>
        ))}
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={playPreview}
          disabled={!!preview}
        >
          <Play className="mr-1 h-3 w-3" />
          Preview squeeze
        </Button>
      </div>

      {/* Exact numbers for whoever draws the artwork. */}
      <div className="rounded-md border bg-muted/40 p-3 text-sm">
        <div className="font-medium">Artwork guide</div>
        <div className="mt-1 text-muted-foreground">
          At {frameWidth}×{frameHeight}: sidebar{" "}
          <span className="font-mono">{bars.sidebar}px</span>, banner{" "}
          <span className="font-mono">{bars.banner}px</span>
          {snapTarget ? ` (${snapTarget.replace("-", " ")})` : ""}
        </div>
      </div>
    </div>
  );
}
